/** Browser desktop-notification + audio-chime primitives for live trade
 * alerts. Framework-free and side-effect-isolated so the hook layer stays
 * thin and the browser APIs are easy to mock in tests.
 *
 * Sound uses the Web Audio API (a short two-note oscillator chime) rather
 * than a bundled audio file — no binary asset, and a distinct tone per
 * event: rising for an entry, falling for an exit. */

import { FILE_PREFIX, findSound, playSound } from "./soundLibrary";

export type ChimeKind = "entry" | "exit";

/** localStorage flag the notification bell writes (NotificationsProvider).
 * Read here so any surface — e.g. the toast mirror — can gate on it without
 * threading React context. */
export const ALERTS_ENABLED_KEY = "nss.notify.enabled";

export function alertsEnabled(): boolean {
  try {
    return typeof localStorage !== "undefined" && localStorage.getItem(ALERTS_ENABLED_KEY) === "1";
  } catch {
    return false; // localStorage absent/blocked (private mode, stubbed in tests)
  }
}

export function notificationsSupported(): boolean {
  return typeof window !== "undefined" && "Notification" in window;
}

export function notificationPermission(): NotificationPermission {
  if (!notificationsSupported()) return "denied";
  return Notification.permission;
}

export async function requestNotificationPermission(): Promise<NotificationPermission> {
  if (!notificationsSupported()) return "denied";
  return Notification.requestPermission();
}

/** Show an OS-level notification. No-op unless permission was granted.
 * `tag` collapses a rapid burst into one toast instead of stacking; pass a
 * distinct tag (e.g. auto-bias zone alerts) so unrelated alerts coexist. */
export function showDesktopNotification(
  title: string,
  body: string,
  tag = "nss-trade-alert",
): void {
  if (!notificationsSupported() || Notification.permission !== "granted") return;
  new Notification(title, { body, tag });
}

/** Loud, sticky notification for a genuine emergency (an EXIT that FAILED —
 * the position may still be OPEN at the broker). `requireInteraction` keeps
 * it on screen until the operator dismisses it, instead of auto-fading like a
 * normal alert. A fresh unique tag means it never collapses into an earlier
 * popup. */
export function showHardNotification(title: string, body: string, tag: string): void {
  if (!notificationsSupported() || Notification.permission !== "granted") return;
  new Notification(title, { body, tag, requireInteraction: true });
}

// --- audio ---------------------------------------------------------------

let audioCtx: AudioContext | null = null;

function ensureAudioContext(): AudioContext | null {
  if (typeof window === "undefined") return null;
  // lib.dom types AudioContext as always-present, but it is absent in some
  // runtimes (older Safari uses webkitAudioContext; jsdom has neither), so
  // read it through a widened type where it can be undefined.
  const w = window as unknown as {
    AudioContext?: typeof AudioContext;
    webkitAudioContext?: typeof AudioContext;
  };
  const Ctor = w.AudioContext ?? w.webkitAudioContext;
  if (Ctor === undefined) return null;
  audioCtx ??= new Ctor();
  return audioCtx;
}

/** Create/resume the AudioContext. MUST run inside a user gesture the first
 * time (browsers block autoplay) — the bell toggle's click is that gesture. */
export function unlockAudio(): void {
  const ctx = ensureAudioContext();
  if (ctx !== null && ctx.state === "suspended") void ctx.resume();
}

export interface ChimePreset {
  id: string;
  label: string;
  /** [low, high] two-note motif. Entry rises low→high and exit falls
   * high→low, so DIRECTION distinguishes entry from exit while the PITCH
   * distinguishes one strategy from another. */
  notes: readonly [number, number];
}

/** Sentinel preset id meaning "play nothing". */
export const MUTE_CHIME = "mute";

/** Fallback when a strategy has no saved choice or an unknown one. */
export const DEFAULT_CHIME = "bright";

export const CHIME_PRESETS: readonly ChimePreset[] = [
  { id: "bright", label: "Bright", notes: [660, 880] },
  { id: "deep", label: "Deep", notes: [330, 440] },
  { id: "triad", label: "Triad", notes: [523, 784] },
  { id: "ping", label: "Ping", notes: [988, 1319] },
];

function resolvePreset(presetId: string): ChimePreset | null {
  if (presetId === MUTE_CHIME) return null;
  const found = CHIME_PRESETS.find((p) => p.id === presetId);
  if (found !== undefined) return found;
  return CHIME_PRESETS.find((p) => p.id === DEFAULT_CHIME) ?? null;
}

/** Play a short two-note chime for `kind`, using the strategy's chosen
 * `presetId`. A muted or unknown-but-mute preset plays nothing; an unknown
 * non-mute id falls back to the default preset. */
export function playChime(kind: ChimeKind, presetId: string = DEFAULT_CHIME): void {
  // a sound from the library (made in the browser, or a published file from the server) plays as it
  // is; the four old two-note chimes keep their rising / falling entry and exit
  const old = CHIME_PRESETS.some((p) => p.id === presetId);
  if (
    !old &&
    presetId !== MUTE_CHIME &&
    (presetId.startsWith(FILE_PREFIX) || findSound(presetId))
  ) {
    playSound(presetId);
    return;
  }
  const preset = resolvePreset(presetId);
  if (preset === null) return;
  const ctx = ensureAudioContext();
  if (ctx === null) return;
  const [low, high] = preset.notes;
  const [first, second] = kind === "entry" ? [low, high] : [high, low];
  playTone(ctx, first, ctx.currentTime, 0.12);
  playTone(ctx, second, ctx.currentTime + 0.13, 0.16);
}

/** Urgent, unmistakable alarm for an exit FAILURE — a repeating high/low
 * two-tone siren (six beeps over ~1.5s) that is deliberately harsher and
 * longer than the normal chime so it cuts through. Ignores the per-strategy
 * chime preset (this is an emergency, not a routine fill) but stays silent if
 * audio is unavailable. */
export function playAlarm(): void {
  const ctx = ensureAudioContext();
  if (ctx === null) return;
  const start = ctx.currentTime;
  // Alternating 880/440 Hz beeps — the classic two-tone "attention" siren.
  for (let i = 0; i < 6; i += 1) {
    const freq = i % 2 === 0 ? 880 : 440;
    playTone(ctx, freq, start + i * 0.24, 0.2);
  }
}

function playTone(ctx: AudioContext, freq: number, start: number, dur: number): void {
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = "sine";
  osc.frequency.value = freq;
  // Quick attack, exponential release — a soft "beep", not a click.
  gain.gain.setValueAtTime(0.0001, start);
  gain.gain.exponentialRampToValueAtTime(0.2, start + 0.01);
  gain.gain.exponentialRampToValueAtTime(0.0001, start + dur);
  osc.connect(gain).connect(ctx.destination);
  osc.start(start);
  osc.stop(start + dur + 0.02);
}
